import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { TopNav } from '../components/navigation/TopNav.jsx'
import { Wordmark } from '../components/core/Wordmark.jsx'
import { Button } from '../components/core/Button.jsx'
import { routes } from '~/marketing/routes'

// The one header, on every public and signed-in page — the founder's call,
// 2026-10-30. Until then the public pages and the app each had their own, so a
// signed-in customer reading `/pricing` saw the visitor's links, and one on the
// Overview had to scroll to the footer to reach Pricing.
//
// **Who is reading decides the links, not which page they are on.** Signed out:
// How it works, Pricing and Methodology, with Sign in and Start free. Signed in,
// on every page: Dashboard, Questions, Plan and Account, with the trial line
// and Sign out. The public pages a customer still wants are in the footer,
// which is the same everywhere. `header_signed_in` is the server's answer, so
// nobody sees the wrong header first; signed out it is false, which is what
// every prerendered page was built with, so a crawler reads the same HTML.
//
// At 640px and up it is `TopNav`. Below, a wordmark and a Menu toggle opening
// a vertical list at 44px rows, with the trial line at the top of the list
// rather than gone. Signed out, "Start free" stays in the bar beside Menu,
// where the public header had it at every width.
//
// **Sign out is in both**, and that is a re-applied fix: the design export had
// none at any width. See `docs/design/app-local-patches.md`.
//
// **`header_trial`, not `trial_chrome`** — renamed 2026-10-03, because "chrome"
// reads as the browser. Reading the old key renders no counter, silently.
//
// **`beforeBusiness`** is the setup page for an account's first business
// (Next Steps #20): `/dashboard` redirects there until a business exists, so
// Dashboard and Questions would loop back to the page already open. Everywhere
// else they stay — for an account with no business they lead to setup, which
// is where that account belongs.
//
// **The logo goes to the home page**, signed in or not, 2026-10-30. And the
// first link says "Dashboard", as the button that used to sit here did; the
// page's own heading stays "Overview".
//
// Wordmark 23 on the bar, not 17: `size` is the *word's* font size and the
// lockup draws 1.90x that tall, so at 17 the word sat 3px above the 14px links
// and read as another link. At 23 the lockup is 44px inside a 56px bar. 19 on a
// phone, where nothing sits beside it but the buttons.
const VISITOR_NAV = [
  { href: routes.home + '#how', label: 'How it works', exact: true },
  { href: routes.pricing, label: 'Pricing', exact: true },
  { href: routes.methodology, label: 'Methodology', exact: true },
]

const CUSTOMER_NAV = [
  { href: routes.dashboard, label: 'Dashboard', exact: true, needsBusiness: true },
  { href: '/dashboard/prompts', label: 'Questions', needsBusiness: true },
  { href: '/billing', label: 'Plan' },
  { href: routes.account, label: 'Account' },
]

function isActive(url, item) {
  const path = url.split('?')[0]
  return item.exact ? path === item.href : path === item.href || path.startsWith(item.href + '/')
}

function trialLine(t) {
  if (!t) return null
  return `Day ${t.day} of ${t.length} · ${t.card_on_file ? 'card on file' : 'no card on file'}`
}

// **A hash on the page you are already on is a scroll, not a navigation.**
// `router.visit('/#how')` from `/` refetches the page and remounts it, which
// resets every `useState` on it — so "How it works" replayed the home page's
// sample report from empty, under a click meant to land further down the page.
// Returning without `preventDefault` hands it to the browser, whose anchor
// scroll honours the target's `scroll-margin-top`. A modified click is left to
// the browser too, so a new tab opens where somebody asked for one.
const visit = (href) => (e) => {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
  const [path, hash] = href.split('#')
  if (hash && path === window.location.pathname) return
  e.preventDefault()
  router.visit(href)
}

export default function SiteHeader({ beforeBusiness = false }) {
  const { url, props } = usePage()
  const [open, setOpen] = useState(false)
  const signedIn = props.header_signed_in === true
  const trial = signedIn ? trialLine(props.header_trial) : null
  const nav = signedIn
    ? CUSTOMER_NAV.filter((item) => !(beforeBusiness && item.needsBusiness))
    : VISITOR_NAV

  // No confirmation: sessions are rows, signing back in costs a password, and a
  // dialog in front of a reversible action is friction pretending to be safety.
  // Deliberately not the reset flow, which destroys every session on purpose.
  const signOut = () => router.delete(routes.signOut)

  const home = (size) => (
    <a href={routes.home} onClick={visit(routes.home)} aria-label="Spruce home" style={{ display: 'inline-flex' }}>
      <Wordmark size={size} />
    </a>
  )

  return (
    <>
      <div className="sticky top-0 z-40 hidden sm:block">
        <TopNav
          brand={home(23)}
          links={nav.map((item) => ({ label: item.label, href: item.href, active: isActive(url, item), onClick: visit(item.href) }))}
          actions={signedIn ? (
            <>
              {trial && (
                <span className="whitespace-nowrap font-mono text-[12px] tabular-nums text-[color:var(--text-muted)]">
                  {trial}
                </span>
              )}
              <Button variant="ghost" size="sm" as="button" type="button" iconLeft="log-out" onClick={signOut}>
                Sign out
              </Button>
            </>
          ) : (
            <>
              <Button variant="link" size="sm" href={routes.signIn}>Sign in</Button>
              {/* Secondary on purpose: every page carries its own amber CTA, and there is one per viewport. */}
              <Button variant="secondary" size="sm" href={routes.signUp}>Start free</Button>
            </>
          )}
        />
      </div>

      <div className="sticky top-0 z-40 sm:hidden">
        <div className="flex h-14 items-center justify-between gap-3 border-b border-[color:var(--border-subtle)] bg-[rgba(20,23,28,.82)] px-4 backdrop-blur-[12px]">
          {home(19)}
          <div className="flex items-center gap-2">
            {!signedIn && <Button variant="secondary" size="sm" href={routes.signUp}>Start free</Button>}
            <button
              type="button"
              aria-expanded={open}
              aria-controls="site-menu"
              onClick={() => setOpen((v) => !v)}
              className="flex h-11 items-center rounded-[7px] px-3 text-[14px] font-medium text-[color:var(--text-strong)] hover:bg-[color:var(--surface-hover)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[rgba(245,166,35,.20)]"
            >
              {open ? 'Close' : 'Menu'}
            </button>
          </div>
        </div>
        {open && (
          <nav
            id="site-menu"
            aria-label="Main"
            className="border-b border-[color:var(--border-subtle)] bg-[color:var(--bg-base)]"
          >
            {trial && (
              <p className="border-b border-[color:var(--border-subtle)] px-4 py-3 font-mono text-[12px] tabular-nums text-[color:var(--text-muted)]">
                {trial}
              </p>
            )}
            <ul className="flex flex-col">
              {nav.map((item) => {
                const active = isActive(url, item)
                return (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      aria-current={active ? 'page' : undefined}
                      onClick={(e) => {
                        setOpen(false)
                        visit(item.href)(e)
                      }}
                      className={`flex min-h-[44px] items-center px-4 text-[15px] ${
                        active
                          ? 'font-medium text-[color:var(--text-strong)]'
                          : 'text-[color:var(--text-body)] hover:bg-[color:var(--surface-hover)]'
                      }`}
                    >
                      {item.label}
                    </a>
                  </li>
                )
              })}
              {/* A row rather than a button, so it is the same 44px target as
                  every link above it. Last, and below a rule, because signing
                  out ends the session and signing in starts one. */}
              <li>
                {signedIn ? (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false)
                      signOut()
                    }}
                    className="flex min-h-[44px] w-full items-center border-t border-[color:var(--border-subtle)] px-4 text-left text-[15px] text-[color:var(--text-body)] hover:bg-[color:var(--surface-hover)]"
                  >
                    Sign out
                  </button>
                ) : (
                  <a
                    href={routes.signIn}
                    onClick={(e) => {
                      setOpen(false)
                      visit(routes.signIn)(e)
                    }}
                    className="flex min-h-[44px] items-center border-t border-[color:var(--border-subtle)] px-4 text-[15px] text-[color:var(--text-body)] hover:bg-[color:var(--surface-hover)]"
                  >
                    Sign in
                  </a>
                )}
              </li>
            </ul>
          </nav>
        )}
      </div>
    </>
  )
}
